import { ReactElement, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PhoneNumberInput, { PhoneNumberInputProps } from '../PhoneNumberInput';
import {
  FeedbackLabelInterface,
  LoginResponseInterface,
  OneTimePasswordInterface,
} from '../../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import { ENVIRONMENTS, OTP_DELIVERY_CHANNELS, OTP_REQUEST_ERROR_CODE } from '../../services/exports/Constants';
import AuthorizationManager, { LoginData, OtpLoginData } from '../../services/auth/AuthorizationManager';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { DataWrappedResponse, Response } from '../../services/exports/Types';
import FeedbackLabel from '../labels/FeedbackLabel';
import useAuth from '../../hooks/global/useAuth';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { resolveOtpFailure } from '../../utils/otp';
import Captcha from './turnstile/Captcha';
import { ITurnstileVerifyPayload, TurnstileType } from './turnstile/types';
import classnames from 'classnames';
import ChannelChooser from './ChannelChooser';
import useOtpChannels from '../../hooks/auth/useOtpChannels';
import OtpVerificationStep from './OtpVerificationStep';
import { mailableLanguage } from '../../services/localization/Languages';

export type OtpFlowHandle = {
  requestCode: (delivery_channel?: OTP_DELIVERY_CHANNELS) => Promise<DataWrappedResponse<OneTimePasswordInterface>>;
  logIn: (data?: Partial<LoginData>) => Promise<Response<LoginResponseInterface>>;
};

interface Props {
  isStatic?: boolean;
  allowChannelSelection?: boolean;
  // In static mode the phone input is hidden by default; set this to show it
  // (disabled) instead — e.g. the checkout phone-verification modal.
  revealPhoneNumber?: boolean;
  initialPhoneNumber?: string;
  extra?: ReactElement;
  onPhoneNumberChange?: (value: string) => void;
  onPasswordChange?: (value: string) => void;
  onCodeRequested?: (value?: string) => void;
  onValidationStatusChange?: (result: boolean) => void;
  hideOtp?: boolean;
  className?: string;
  phoneNumberLabel?: ReactElement;
  phoneNumberInputProps?: Partial<PhoneNumberInputProps>;
}

const OTP_LENGTH = 4;
// Fallback resend lockout used only when the response omits a `retry-after` header.
const DEFAULT_RESEND_LOCKOUT_SECONDS = 30;
// Deliberately loose — the backend validates with `email:strict`; this only stops an
// obviously incomplete address from being submitted.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const TURNSTILE_ENABLED =
  ['true', true].includes(import.meta.env.VITE_TURNSTILE_ENABLED) || import.meta.env.VITE_MODE === ENVIRONMENTS.PROD;
export const TURNSTILE_VERIFICATION_TOKEN_HEADER = 'x-turnstile-token';
export const TURNSTILE_TYPE_HEADER = 'x-turnstile-type';

const OtpFlow = forwardRef<OtpFlowHandle, Props>((props: Props, ref: any) => {
  useImperativeHandle(ref, () => ({ requestCode, logIn }));

  const { t, i18n } = useTranslation(null, { keyPrefix: 'Components:Auth:OtpFlow' });

  const {
    isStatic,
    allowChannelSelection,
    revealPhoneNumber,
    initialPhoneNumber,
    extra,
    onPhoneNumberChange,
    onPasswordChange,
    onCodeRequested,
    onValidationStatusChange,
    hideOtp,
    className,
    phoneNumberLabel,
    phoneNumberInputProps,
  } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);

  const { channels, defaultChannel } = useOtpChannels();
  const { login } = useAuth();
  const { trackOtpRequested, trackOtpResendRequested, trackOtpVerified, trackOtpVerificationFailed } = useAnalytics();
  const { localData } = useOrderFlow();
  const otpRef = useRef(null);
  const resendCount = useRef(0);

  const [phoneNumber, setPhoneNumber] = useState(
    initialPhoneNumber ?? profile?.phone_number ?? localData.customer_phone_number,
  );
  const [country, setCountry] = useState(null);
  const [code, setCode] = useState(null);
  const [otp, setOtp] = useState<OneTimePasswordInterface>(null);
  const [turnstilePayload, setTurnstilePayload] = useState<ITurnstileVerifyPayload>({
    token: null,
    type: TurnstileType.Cloudflare,
  });

  const [codeRequested, setCodeRequested] = useState(false);
  const [resending, setResending] = useState(false);
  const [retrySmsAfter, setRetrySmsAfter] = useState(null);
  // Pre-selected channel comes from the provider, not a constant: an email-only partner
  // offers no WhatsApp, and defaulting to it would make the first request a 422.
  // Analytics tracks whether the user kept this default or switched away from it.
  const [selectedChannel, setSelectedChannel] = useState<OTP_DELIVERY_CHANNELS>(defaultChannel);
  const [email, setEmail] = useState<string>(profile?.email ?? '');
  const [lastSentChannel, setLastSentChannel] = useState<OTP_DELIVERY_CHANNELS>(null);
  const [feedback, setFeedback] = useState<FeedbackLabelInterface>({
    message: '',
  });

  // Auto-send for the static/no-chooser callers (guest cashback claims). Gated on there
  // being an address to send to: on the email channel this branch has no chooser to reveal
  // the field, so firing regardless posted an empty address and dead-ended on a 422.
  useEffect(() => {
    isStatic &&
      !allowChannelSelection &&
      hasDeliverableAddress() &&
      (!TURNSTILE_ENABLED || (!codeRequested && Boolean(turnstilePayload.token))) &&
      requestCode(selectedChannel);
  }, [turnstilePayload.token]);

  // S4: `initialData` is persisted, so the first paint can run against a cached company
  // that predates `otp_delivery_channels` and default to a channel this shop does not
  // offer. When the fresh company lands the chooser re-renders, but a stale selection
  // would survive it — leaving no address field and a request that can only 422.
  useEffect(() => {
    if (!channels.includes(selectedChannel)) {
      setSelectedChannel(defaultChannel);
    }
  }, [channels, selectedChannel, defaultChannel]);

  useEffect(() => {
    onPhoneNumberChange && onPhoneNumberChange(phoneNumber);
  }, [phoneNumber]);

  useEffect(() => {
    onPasswordChange && onPasswordChange(code);
  }, [code]);

  useEffect(() => {
    if (codeRequested) {
      scrollToOTP();
    }
  }, [codeRequested]);

  // Tick the resend lockout down to zero. Keyed on the boolean so the interval is created once
  // per lockout (not re-created every second). Mirrors the mobile useOtpFlow countdown.
  const isResendLocked = retrySmsAfter > 0;
  useEffect(() => {
    if (!isResendLocked) {
      return;
    }

    const timer = setInterval(() => setRetrySmsAfter((current) => (current <= 1 ? 0 : current - 1)), 1000);

    return () => clearInterval(timer);
  }, [isResendLocked]);

  useEffect(() => {
    onValidationStatusChange && onValidationStatusChange(validateInput());
  }, [codeRequested, code, phoneNumber, turnstilePayload.token, hideOtp, email, selectedChannel]);

  const scrollToOTP = () => {
    otpRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Pre-send the widget is shown so the user can solve before the initial request. Once a
  // code has been requested the widget reappears above the resend buttons when the resend
  // timer elapses; a resend must carry a freshly-solved token (each one is single-use), so
  // we clear the consumed token after every successful request and toast if it's missing.
  async function requestCode(
    delivery_channel: OTP_DELIVERY_CHANNELS = undefined,
  ): Promise<DataWrappedResponse<OneTimePasswordInterface>> {
    const channel = delivery_channel ?? selectedChannel;
    // A code was already issued for this flow → this call is a resend, not the initial request.
    const isResend = codeRequested;

    if (isResend && TURNSTILE_ENABLED && !turnstilePayload.token) {
      toast.error(t('error_messages.turnstile_required'));

      return;
    }

    if (resending) {
      return;
    }

    setResending(true);

    const request = await AuthorizationManager.postOptCode(
      {
        company_id: company?.id,
        phone_number: phoneNumber,
        lang: mailableLanguage(i18n.language),
        delivery_channel: channel,
        // Only sent on the email channel; the backend ignores an address on the phone
        // channels and requires one here for a customer it has not seen before.
        ...(channel === OTP_DELIVERY_CHANNELS.EMAIL && { email }),
        country,
      },
      { [TURNSTILE_VERIFICATION_TOKEN_HEADER]: turnstilePayload.token, [TURNSTILE_TYPE_HEADER]: turnstilePayload.type },
    );

    const { response, success } = request;
    setResending(false);

    if (success) {
      if (isResend) {
        resendCount.current += 1;
        trackOtpResendRequested({
          otp_channel: channel,
          switched_on_resend: channel !== lastSentChannel,
          resend_number: resendCount.current,
        });
      } else {
        trackOtpRequested({
          otp_channel: channel,
          channel_is_default: channel === defaultChannel,
        });
      }

      setCodeRequested(true);
      setOtp(response.data.data);
      setLastSentChannel(channel);
      setSelectedChannel(channel);
      setRetrySmsAfter(response?.headers['retry-after'] ?? DEFAULT_RESEND_LOCKOUT_SECONDS);
      // The just-used token is consumed; a resend must re-solve the widget for a new one.
      setTurnstilePayload({ token: null, type: TurnstileType.Cloudflare });

      if (import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD) {
        setCode(response?.data?.data?.password);
      }

      resetErrorMessage();
      import.meta.env.VITE_MODE === ENVIRONMENTS.PROD && toast.success(t('toasts.code_sent'));
      setTimeout(() => onCodeRequested && onCodeRequested(phoneNumber));
      scrollToOTP();

      return request;
    }

    requestCodeErrorHandler(response);

    return request;
  }

  async function logIn(data: Partial<OtpLoginData> = {}): Promise<Response<LoginResponseInterface>> {
    const result = await login({
      company_id: company?.id,
      phone_number: phoneNumber,
      password: code,
      ...data,
    });

    const otp_channel = lastSentChannel ?? selectedChannel;

    if (result.success) {
      // Fire before the post-login identify() runs so Mixpanel merges the anonymous OTP funnel.
      trackOtpVerified({ otp_channel });
    } else {
      trackOtpVerificationFailed({ otp_channel, ...resolveOtpFailure(result) });
    }

    return result;
  }

  function requestCodeErrorHandler(error: any) {
    const status = error?.response?.status;
    // Both messages name the destination, so they have to follow the channel: on email
    // every rejection used to read "Invalid number. Please try again.", which points the
    // customer at the one field that was fine.
    const onEmail = selectedChannel === OTP_DELIVERY_CHANNELS.EMAIL;

    switch (status) {
      case 429:
        setRetrySmsAfter(error?.response?.headers['retry-after'] ?? null);
        setFeedback({
          type: 'Error',
          message: t(onEmail ? 'error_messages.frequent_requests_email' : 'error_messages.frequent_requests'),
        });
        break;
      case 422: {
        // An existing account with no email on file cannot use this channel: the fix is a
        // support request to have one attached, not a retyped address — so the copy names
        // the provider's support email instead of blaming the field.
        if (error?.response?.data?.error_code === OTP_REQUEST_ERROR_CODE.EMAIL_NOT_ON_FILE) {
          setFeedback({
            type: 'Error',
            message: t('error_messages.email_not_on_file', {
              support_email: error.response.data.payload?.support_email ?? company?.service_provider?.support_email,
            }),
          });
          break;
        }

        setFeedback({
          type: 'Error',
          message: t(onEmail ? 'error_messages.invalid_email' : 'error_messages.invalid_number'),
        });
        break;
      }

      default:
        setFeedback({
          type: 'Error',
          message: t('error_messages.generic_error'),
        });
        break;
    }

    setTimeout(() => onCodeRequested && onCodeRequested());
  }

  function validateInput(): boolean {
    // When the OTP input is hidden we're on the phone-entry step — validate the phone (and
    // turnstile), not the code. `hideOtp` covers navigating back to the phone step after a
    // code was already requested (codeRequested stays true), which otherwise locks the button.
    if (!codeRequested || hideOtp) {
      return (
        phoneNumber?.length >= 10 && hasDeliverableAddress() && (!TURNSTILE_ENABLED || Boolean(turnstilePayload.token))
      );
    }

    return phoneNumber.length >= 10 && code?.length >= OTP_LENGTH;
  }

  // The phone stays the identity key on every channel; email is only the address the
  // code goes to, so it is required exactly when that is the chosen channel.
  function hasDeliverableAddress(): boolean {
    return selectedChannel !== OTP_DELIVERY_CHANNELS.EMAIL || EMAIL_PATTERN.test(email);
  }

  function resetErrorMessage() {
    setFeedback({
      message: '',
    });
  }

  // The email channel is the only one whose destination the customer has to supply.
  const needsAddress = selectedChannel === OTP_DELIVERY_CHANNELS.EMAIL;

  const emailInput =
    selectedChannel === OTP_DELIVERY_CHANNELS.EMAIL ? (
      <div className="mt-sm text-left">
        <label htmlFor="otp_email">{t('form.email.label')}</label>
        <input
          id="otp_email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={t('form.email.placeholder')}
          className={emailInputStyle}
          data-test="otp-email"
        />
      </div>
    ) : null;

  const turnstile = useMemo(() => {
    if (!TURNSTILE_ENABLED) {
      return null;
    }

    return <Captcha onVerify={setTurnstilePayload} className="mt-md" />;
  }, []);

  return (
    <div className={className}>
      {Boolean(feedback?.message) && <FeedbackLabel message={feedback?.message} className="mb-md" type="Error" />}

      {/* Phone input — hidden in static mode; with `revealPhoneNumber` it's shown disabled, but
          only during the pre-send (channel selection) step, not once the code has been requested */}
      <div className={isStatic && !(revealPhoneNumber && !codeRequested) ? 'hidden' : ''}>
        <label htmlFor="phone_number">{phoneNumberLabel ?? t('form.phone_number.label')}</label>
        <PhoneNumberInput
          placeholder={t('form.phone_number.placeholder')}
          value={phoneNumber}
          onChange={setPhoneNumber}
          onChangeCountry={setCountry}
          initialCountry={country}
          disabled={isStatic}
          id="phone_number"
          className="mt-xs"
          {...phoneNumberInputProps}
        />
        {!codeRequested && !isStatic && (
          <>
            <ChannelChooser value={selectedChannel} onChange={setSelectedChannel} className="mt-sm" />
            {emailInput}
          </>
        )}
        {/* Render on the phone-entry step — including navigating back to it (hideOtp) after a
            code was requested — so the user can (re)solve the captcha before continuing. */}
        {!isStatic && (!codeRequested || hideOtp) && turnstile}
      </div>

      {/* Static mode without the chooser: Turnstile is visible before the code is requested,
          and on the email channel the address field comes with it — this branch has no
          chooser to reveal it, and the auto-send above waits for a valid address. The send
          button appears with the field because that effect is keyed on the captcha token
          and will not re-fire when the address is typed. */}
      {isStatic && !codeRequested && !allowChannelSelection && (
        <div className="space-y-sm">
          {needsAddress && emailInput}
          {turnstile}
          {needsAddress && (
            <button
              type="button"
              onClick={() => requestCode(selectedChannel)}
              disabled={!validateInput() || resending}
              className={sendCodeButton}
              data-test="otp-send-code"
            >
              {t('otp.send_code')}
            </button>
          )}
        </div>
      )}

      {/* Static channel chooser — let the user pick WhatsApp/SMS before the code is sent */}
      {isStatic && allowChannelSelection && !codeRequested && (
        <div className={classnames('space-y-sm', revealPhoneNumber && 'mt-sm')}>
          <ChannelChooser value={selectedChannel} onChange={setSelectedChannel} />
          {emailInput}
          {turnstile}
          <button
            type="button"
            onClick={() => requestCode(selectedChannel)}
            disabled={!validateInput() || resending}
            className={sendCodeButton}
            data-test="otp-send-code"
          >
            {t('otp.send_code')}
          </button>
        </div>
      )}

      {/* OTP verification section */}
      {codeRequested && !hideOtp && (
        <div ref={otpRef}>
          <OtpVerificationStep
            code={code}
            onCodeChange={setCode}
            phoneNumber={phoneNumber}
            deliveryAddress={otp?.delivery_address}
            channel={lastSentChannel ?? selectedChannel}
            onResend={requestCode}
            retryAfter={retrySmsAfter ?? 0}
            resending={resending}
            turnstile={turnstile}
            shouldAutoFocus
          >
            {extra}
          </OtpVerificationStep>
        </div>
      )}
    </div>
  );
});

export default OtpFlow;

// Styles
const emailInputStyle =
  'mt-xs w-full rounded-lg border-[1px] border-brand-border bg-card px-sm py-sm text-sm text-brand-text-primary placeholder:text-muted-foreground focus:border-brand-primary focus:outline-none';
const sendCodeButton =
  'w-full rounded-full bg-brand-primary px-sm py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-60';
